import React, { useMemo } from "react";
import LinesEllipsis from "react-lines-ellipsis";
import DOMPurify from "dompurify";

import { Media } from "src/types/media";
import Thumbnail from "src/components/thumbnail";

import styles from "./card.module.scss";
import _ from "lodash";
import cx from "classnames";
import dateFormat from "dateformat";

import TimeAgo from "react-timeago";
import { LearningRecordCourseSummary, LearningRecordSimulationSummary, LearningRecordSummary } from "src/types/coaching/learners";

import { FileService } from "src/services/file.service";
import { CALL_TYPES } from "src/constants/simulation";
import Text from "src/components/text";

interface CourseCardProps {
  course: LearningRecordSummary;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED";
  // eslint-disable-next-line no-unused-vars
  onClick: (course: LearningRecordSummary) => void;
}

const defaultImage = "/images/collections/background.jpeg";

const AssignedCourseCard: React.FC<CourseCardProps> = ({ course, status, onClick }) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const description = useMemo(() => _.get(course, "course.description", ""), [course]);
  const icon = useMemo(() => _.get(course, "course.banner.background") as Media, [course]);
  const assignedBy = useMemo(() => _.get(course, "assignedBy") as any, [course]);
  const createdDate = useMemo(() => _.get(course, "createdDate") as any, [course]);
  const duration = useMemo(() => _.get(course, 'course.duration') || 0, [course]);

  const callType = useMemo(() => {
      const value = _.get(course, "course.callType") || "";
      return CALL_TYPES.find((item) => item.id === value)?.title || "";
    }, [course]);

  return (
    <div className={cx(styles.card, styles.assigned)} onClick={() => onClick(course)}>
      <div className={cx(styles.header)}>
        <Thumbnail className={styles.icon} media={icon} defaultImage={defaultImage} width={600} />
        {assignedBy &&
          <div className={styles.assignedBy}>
            <div className={styles.details}>
              <div className={styles.date}><TimeAgo date={createdDate} /></div>
              <div className={styles.name}>{assignedBy.firstName || ""} {assignedBy.lastName || ""}</div>
            </div>
            <Thumbnail className={styles.icon} media={assignedBy.icon} defaultImage={defaultImage} width={32} />
          </div>
        }
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1} />
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          {course.course.entityType === "course" &&
            <>
              <div className={styles.metadata}>
                <i className="paperflite-file"></i>
                <span>{((course.course) as LearningRecordCourseSummary).lessons || 0} Lessons</span>
              </div>
              <div className={styles.border} />
              <div className={styles.metadata}>
                <i className="paperflite-assessment"></i>
                <span>{((course.course) as LearningRecordCourseSummary).assessments || 0} Assessments</span>
              </div>
              {duration > 0 && (
                <>
                  <div className={styles.border} />
                  <div className={styles.metadata}>
                    <i className="paperflite-clock"></i>
                    <span>{FileService.formatDuration(duration, 'long')}</span>
                  </div>
                </>
              )}
            </>
          }

          {course.course.entityType === "simulation" &&
            <>
              {duration > 0 && (
                <>
                  <div className={styles.metadata}>
                    <i className="paperflite-clock"></i>
                    <span>{duration} Minutes</span>
                  </div>
                  <div className={styles.border} />
                </>
              )}
              <div className={styles.metadata}>
                <i className="paperflite-user-settings"></i>
                <span>{((course.course) as LearningRecordSimulationSummary).persona?.designation || ""}</span>
              </div>
              <div className={styles.border} />
              <div className={styles.metadata}>
                <i className="paperflite-rocket"></i>
                <span>{callType}</span>
              </div>
            </>
          }
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action, styles.start)}>
            <i className="paperflite-down-arrow" />
            <span>Start Now</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const InProgressCourseCard: React.FC<CourseCardProps> = ({ course, onClick }) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const description = useMemo(() => _.get(course, "course.description", ""), [course]);
  const icon = useMemo(() => _.get(course, "course.banner.background") as Media, [course]);
  const duration = useMemo(() => course.duration || 0, [course]);

  const currentContent = useMemo(() => {
    const currentContent = _.get(course, "currentContent") as any;
    const name = _.get(currentContent, "name");
    const type = _.get(currentContent, "entityType");
    if (type === "lesson") {
      return <><b>Continue Lesson:</b> <Text className={styles.value} text={name || ""} lines={1} /></>;
    } else if (type === "assessment") {
      return <><b>Continue Assessment:</b> {name}</>;
    }

    return null;
  }, [course]);

  return (
    <div className={cx(styles.card, styles.in_progress)} onClick={() => onClick(course)}>
      <div className={cx(styles.header)}>
        <Thumbnail className={styles.icon} media={icon} defaultImage={defaultImage} width={600} />
        {duration > 0 && (
          <div className={styles.timeRemaining}>
            {FileService.formatDuration(duration, 'short')} remaining
          </div>
        )}
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1} />
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          {currentContent && (
            <div className={cx(styles.metadata, styles.currentCourse)}>
              {currentContent}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action, styles.start)}>
            <i className="paperflite-down-arrow" />
            <span>continue learning</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const CompletedCourseCard: React.FC<CourseCardProps> = ({ course, onClick }) => {
  const name = useMemo(() => _.get(course, "course.name", ""), [course]);
  const description = useMemo(() => _.get(course, "course.description", ""), [course]);
  const icon = useMemo(() => _.get(course, "course.banner.background") as Media, [course]);

  const score = useMemo(() => {
    const score = _.get(course, "score") || 0;
    return (score * 100).toFixed(0);
  }, [course]);

  return (
    <div className={cx(styles.card, styles.assigned)} onClick={() => onClick(course)}>
      <div className={cx(styles.header)}>
        <Thumbnail className={styles.icon} media={icon} defaultImage={defaultImage} width={600} />
        <div className={styles.score}>
          Secured <span>{score}%</span>
          <i className='paperflite-certificate'></i>
        </div>
      </div>
      <div className={styles.details}>
        <Text className={styles.name} text={name || ""} lines={1}/>
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-completed"></i>
            <span>completed on {dateFormat(course.completedDate, 'dd mmm yyyy')}</span>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={cx(styles.action, styles.start)}>
            <i className="paperflite-down-arrow" />
            <span>View report</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const CourseCard: React.FC<CourseCardProps> = ({ course, status, onClick }) => {
  switch (status) {
    case "NOT_STARTED":
      return <AssignedCourseCard course={course} status={status} onClick={onClick} />;
    case "IN_PROGRESS":
      return <InProgressCourseCard course={course} status={status} onClick={onClick} />;
    case "COMPLETED":
      return <CompletedCourseCard course={course} status={status} onClick={onClick} />;
    default:
      return null;
  }
};

const PlaceholderCourseCard: React.FC = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.header)}>
        <div className={cx(styles.icon, styles.block)}></div>
      </div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <p className={cx(styles.description)}>
          <div className={styles.block}></div>
          <div className={styles.block} style={{ width: '65%' }}></div>
        </p>
      </div>
      <div className={styles.footer}>
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <div className={styles.block}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { CourseCard, PlaceholderCourseCard };